import { useMultiEmailInput } from '@consigli/hooks';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { RxCross2 } from 'react-icons/rx';

export interface MultiEmailInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  /**
   * Per-email validator. Return a non-empty error string to reject, or undefined to accept.
   * The caller is responsible for translation; the message is rendered verbatim.
   */
  validateEmail?: (email: string, current: string[]) => string | undefined;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Aggregate form-level error. Shown only when there are no chips. */
  formError?: string;
}

export const MultiEmailInput = ({
  value,
  onChange,
  validateEmail,
  label,
  placeholder,
  disabled = false,
  formError,
}: MultiEmailInputProps) => {
  const { t } = useTranslation();
  const { draft, draftError, onDraftChange, onKeyDown, onBlur, onPaste, removeAt } =
    useMultiEmailInput({
      value,
      onChange,
      validateEmail,
      duplicateMessage: t('invite-user.duplicate-email'),
      invalidEmailMessage: t('invite-user.valid-email'),
    });

  const showFormError = !draftError && Boolean(formError) && value.length === 0;
  const hasError = Boolean(draftError) || showFormError;

  return (
    <div className="w-full">
      {label && (
        <label htmlFor="recipients-input" className="block text-sm font-medium mb-1">
          {label}
        </label>
      )}
      <div
        className={clsx(
          'flex flex-wrap items-center gap-2 min-h-[42px] w-full border rounded px-2 py-1 bg-day-light-5',
          {
            'border-error-dark': hasError,
            'border-day-light-2': !hasError,
          },
        )}
      >
        {value.map((email, index) => (
          <span
            key={email}
            data-testid="recipient-chip"
            className="inline-flex items-center gap-1 px-2 py-0.5 bg-day-light-3 text-day-neutral-dark rounded-full text-sm"
          >
            {email}
            <button
              type="button"
              aria-label={`${t('invite-user.remove-email')}: ${email}`}
              disabled={disabled}
              onClick={() => removeAt(index)}
              className="text-day-neutral-subtle hover:text-day-neutral-dark"
            >
              <RxCross2 fontSize={14} />
            </button>
          </span>
        ))}
        <input
          id="recipients-input"
          type="email"
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          onPaste={onPaste}
          disabled={disabled}
          placeholder={value.length === 0 ? placeholder : ''}
          aria-label={label ?? t('invite-user.recipients-label')}
          aria-invalid={hasError}
          className="flex-1 min-w-[180px] bg-transparent outline-hidden text-sm py-1"
        />
      </div>
      {(draftError || showFormError) && (
        <div role="alert" className="text-sm my-2 text-error-dark">
          {draftError ?? formError}
        </div>
      )}
    </div>
  );
};
